import posthog from 'posthog-js';
import { MutableRefObject, useMemo } from 'react';

import { Kbd } from '~/modules/elements/akui/kbd';
import { Menu } from '~/modules/elements/akui/menu';
import { NavRemoteControl } from '~/modules/elements/nav-controls';
import { VideoPlayerRef } from '~/modules/elements/video-player/index';
import GameState from '~/modules/game-engine/game-state/game-state';
import useKeyboardNav, { KeyboardNavContext } from '~/modules/hooks/use-keyboard-nav';
import getSkipIntroTime, { SKIP_INTRO_MS } from '~/modules/songs/utils/get-skip-intro-time';
import getSongFirstNoteMs from '~/modules/songs/utils/get-song-first-note-ms';
import songHasLongIntro from '~/modules/utils/song-has-long-intro';
import CountdownRing from '~/routes/game/singing/game-overlay/components/countdown-ring';
import { MobilePhoneModeSetting, useSettingValue } from '~/routes/settings/settings-state';

interface Props {
  playerRef: MutableRefObject<VideoPlayerRef | null>;
  isEnabled: boolean;
  /** Overrides the local seek — online mode routes the skip through the room server instead. */
  onSkip?: (targetTimeSec: number) => void;
  /** Opens the pause menu — surfaced as the mirrored keyboard's Back control (the TV still uses Backspace). */
  onOpenPauseMenu?: () => void;
}

function SkipIntro({ playerRef, isEnabled, onSkip, onOpenPauseMenu }: Props) {
  'use no memo'; // React Compiler: shouldBeVisible depends on GameState.getCurrentTime(), read directly during render, while playerRef/isEnabled props stay stable, so the compiler's auto-memo bails out and the skip-intro prompt freezes at its first computed visibility.
  const [mobilePhoneMode] = useSettingValue(MobilePhoneModeSetting);
  const song = GameState.getSong()!;
  const hasLongIntro = useMemo(() => songHasLongIntro(song), [song]);
  const songFirstNoteMs = useMemo(() => getSongFirstNoteMs(song), [song]);
  const currentTime = GameState.getCurrentTime(false);
  const shouldBeVisible = currentTime < songFirstNoteMs - SKIP_INTRO_MS * 1.2;

  const canSkip = isEnabled && shouldBeVisible && hasLongIntro;

  // The number is the wait itself; the ring is how long there is left to decide, which runs out
  // exactly as the prompt goes - the prompt stops showing `SKIP_INTRO_MS * 1.2` before the first
  // note, because by then skipping would land after it.
  const secondsUntilSinging = Math.ceil(Math.max(0, songFirstNoteMs - currentTime) / 1000);
  const decisionWindowMs = Math.max(1, songFirstNoteMs - SKIP_INTRO_MS * 1.2);

  const skipIntro = () => {
    const target = getSkipIntroTime(song);
    if (onSkip) {
      onSkip(target);
    } else {
      playerRef.current?.seekTo(target);
    }

    const { artist, title } = GameState.getSong()!;
    posthog.capture('introSkipped', { name: `${artist} - ${title}`, artist, title });
  };

  // A one-action mirror: the prompt element carries the skip action (so physical Enter still skips),
  // plus a remote-only Back that opens the pause menu — the phone otherwise has no Back in mirror mode.
  const { register } = useKeyboardNav({
    enabled: canSkip,
    title: 'Skip intro',
    primary: 'skip-intro',
    titleIcon: 'play',
  });
  // `focused` is stripped: the prompt is a plain HelpText, not a menu item, so it would leak onto the
  // DOM node — the mirror descriptor and physical-Enter handler are all we need from register here.
  const { focused: _focused, ...skipNav } = register('skip-intro', skipIntro, 'Skip intro', true, {
    control: { type: 'button', label: 'Skip intro', icon: 'fastForward' },
  });

  return !mobilePhoneMode && canSkip ? (
    <KeyboardNavContext value={register}>
      <div className="pointer-events-none fixed inset-0 z-[4] flex flex-col items-center justify-center gap-7">
        <CountdownRing
          progress={(decisionWindowMs - currentTime) / decisionWindowMs}
          label={String(secondsUntilSinging)}
          sub="seconds"
        />
        <span className="stroke-text text-[1.875rem] font-bold text-white/75">until the singing starts</span>
        <Menu.HelpText
          {...skipNav}
          className="w-full p-2 text-center text-[1.875rem] [text-shadow:0_0_2rem_black]"
          data-test="skip-intro-info">
          Press <Kbd>Enter</Kbd> to skip the intro
        </Menu.HelpText>
      </div>
      {onOpenPauseMenu && (
        <NavRemoteControl
          name="skip-intro-pause"
          control={{ type: 'button', label: 'Pause game', icon: 'pause' }}
          onClick={onOpenPauseMenu}
        />
      )}
    </KeyboardNavContext>
  ) : null;
}

export default SkipIntro;
